"use client";

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

const LINES = [
    { prompt: true, text: "whoami" },
    { prompt: false, text: "Tran Van Hung" },
    { prompt: true, text: "cat role.txt" },
    { prompt: false, text: "Java Backend Developer" },
    { prompt: true, text: "cat education.txt" },
    { prompt: false, text: "PTIT — Telecommunications Electronics (IoT)" },
    { prompt: true, text: "ls skills/" },
    {
        prompt: false,
        text: "Java  SpringBoot  TypeScript  NodeJS  PostgreSQL  MongoDB  Docker  Jenkins",
    },
    { prompt: true, text: "echo $LOCATION" },
    { prompt: false, text: "Van Quan, Ha Dong, Ha Noi 🇻🇳" },
    { prompt: true, text: "echo $STATUS" },
    { prompt: false, text: "Open for opportunities ✨" },
];

// Typing speed (ms per character) — with randomness for realism
const PROMPT_CHAR_SPEED = 90;   // Base speed for commands (human typing)
const PROMPT_CHAR_JITTER = 40;  // ±40ms random variation
const OUTPUT_CHAR_SPEED = 30;   // Base speed for output (stdout)
const OUTPUT_CHAR_JITTER = 10;  // ±10ms random variation
const PAUSE_AFTER_PROMPT = 500; // Pause after finishing a command before output
const PAUSE_AFTER_OUTPUT = 700; // Pause after output before next command

export function TerminalWindow() {
    // Track which line we're on and how many chars are visible
    const [currentLine, setCurrentLine] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [completedLines, setCompletedLines] = useState<string[]>([]);
    const [isInView, setIsInView] = useState(false);

    const line = LINES[currentLine];
    const isDone = currentLine >= LINES.length;

    const advanceToNextLine = useCallback(() => {
        if (!line) return;
        // Add completed line to history
        const prefix = line.prompt ? "❯ " : "  ";
        setCompletedLines((prev) => [...prev, prefix + line.text]);
        setCharIndex(0);
        setCurrentLine((l) => l + 1);
    }, [line]);

    useEffect(() => {
        if (!isInView || isDone || !line) return;

        const fullText = line.text;

        if (charIndex < fullText.length) {
            // Still typing current line — add random jitter for realism
            const base = line.prompt ? PROMPT_CHAR_SPEED : OUTPUT_CHAR_SPEED;
            const jitter = line.prompt ? PROMPT_CHAR_JITTER : OUTPUT_CHAR_JITTER;
            const speed = base + (Math.random() * 2 - 1) * jitter;
            const timer = setTimeout(() => {
                setCharIndex((c) => c + 1);
            }, speed);
            return () => clearTimeout(timer);
        } else {
            // Line fully typed — pause then advance
            const pause = line.prompt ? PAUSE_AFTER_PROMPT : PAUSE_AFTER_OUTPUT;
            const timer = setTimeout(advanceToNextLine, pause);
            return () => clearTimeout(timer);
        }
    }, [isInView, isDone, line, charIndex, advanceToNextLine]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onViewportEnter={() => setIsInView(true)}
            className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
        >
            {/* Title bar */}
            <div className="flex items-center gap-2 bg-[#1a1a2e]/90 px-4 py-3">
                <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
                <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
                <span className="h-3 w-3 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-xs text-dev-text-muted font-mono">
                    hung@portfolio ~ %
                </span>
            </div>

            {/* Terminal body */}
            <div className="bg-[#0d1117]/90 p-5 font-mono text-sm leading-relaxed min-h-[280px]">
                {/* Already completed lines */}
                {completedLines.map((text, i) => {
                    const isPromptLine = text.startsWith("❯");
                    return (
                        <div key={i} className="flex gap-2">
                            {isPromptLine ? (
                                <>
                                    <span className="text-dev-emerald select-none">❯</span>
                                    <span className="text-dev-text-primary">{text.slice(2)}</span>
                                </>
                            ) : (
                                <span className="text-dev-cyan pl-4">{text.slice(2)}</span>
                            )}
                        </div>
                    );
                })}

                {/* Currently typing line */}
                {!isDone && line && (
                    <div className="flex gap-2">
                        {line.prompt ? (
                            <>
                                <span className="text-dev-emerald select-none">❯</span>
                                <span className="text-dev-text-primary">
                                    {line.text.slice(0, charIndex)}
                                </span>
                                <span className="inline-block h-4 w-2 animate-pulse bg-dev-text-primary translate-y-[2px]" />
                            </>
                        ) : (
                            <span className="text-dev-cyan pl-4">
                                {line.text.slice(0, charIndex)}
                                {charIndex < line.text.length && (
                                    <span className="inline-block h-4 w-[2px] animate-pulse bg-dev-cyan translate-y-[2px] ml-[1px]" />
                                )}
                            </span>
                        )}
                    </div>
                )}

                {/* Blinking cursor after all done */}
                {isDone && (
                    <div className="flex gap-2">
                        <span className="text-dev-emerald select-none">❯</span>
                        <span className="inline-block h-4 w-2 animate-pulse bg-dev-text-primary translate-y-[2px]" />
                    </div>
                )}
            </div>
        </motion.div>
    );
}
